/**
 * Shared viewport / layout width helpers (no React, no device-detect).
 * Used by useResponsive and the react-device-detect alias shim.
 *
 * - Viewport width: raw `window.innerWidth` (chat docking eligibility, clamps).
 * - Layout width: viewport minus docked chat space — drives mobile/tablet/
 *   desktop shell so the header/menu adapt when the chat eats horizontal room.
 */

export const COMFORTABLE_DESKTOP_MIN_WIDTH = 1440;
export const DESKTOP_MIN_WIDTH = 1180;
export const TABLET_MIN_WIDTH = 768;

/** Raw browser width (ignores docked chat). */
export const getViewportWidth = () => {
  if (typeof window === 'undefined') return DESKTOP_MIN_WIDTH;
  return window.innerWidth;
};

/** Pixels reserved on the right while `body.chat-docked` is active. */
export const getChatDockSpace = () => {
  if (typeof document === 'undefined') return 0;
  if (!document.body.classList.contains('chat-docked')) return 0;
  const raw = document.body.style.getPropertyValue('--chat-dock-space');
  const fromInline = parseFloat(raw);
  if (Number.isFinite(fromInline) && fromInline > 0) return fromInline;
  try {
    const computed = parseFloat(
      getComputedStyle(document.body).getPropertyValue('--chat-dock-space'),
    );
    return Number.isFinite(computed) && computed > 0 ? computed : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Width available to the app chrome (header / sider / content).
 * When chat is docked this is viewport − chat space.
 */
export const getLayoutWidth = () => {
  if (typeof window === 'undefined') return DESKTOP_MIN_WIDTH;
  return Math.max(0, getViewportWidth() - getChatDockSpace());
};

/** @deprecated Prefer getLayoutWidth — kept as the responsive alias. */
export const getWindowWidth = getLayoutWidth;

/** Burger / mobile shell (layout width < 1180). */
export const getIsMobileLayout = () => getLayoutWidth() < DESKTOP_MIN_WIDTH;

/** Phone band (layout width < 768). */
export const getIsPhone = () => getLayoutWidth() < TABLET_MIN_WIDTH;

export const getIsTablet = () => {
  const width = getLayoutWidth();
  return width >= TABLET_MIN_WIDTH && width < DESKTOP_MIN_WIDTH;
};

export const getIsDesktop = () => getLayoutWidth() >= DESKTOP_MIN_WIDTH;

/** True desktop viewport for chat docking (ignores docked chat width). */
export const getIsViewportDesktop = () => getViewportWidth() >= DESKTOP_MIN_WIDTH;
